import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { JobsService } from '../services/jobs.service';

/**
 * Route guard that enforces valid job state before entering the affidavit-signing page.
 *
 * @remarks
 * Prevents direct URL access to `/affidavit/:id` for jobs that are not in a signable
 * state. Submitting an affidavit for an already-approved or unserved job produces a
 * stuck 400 item in the upqueue — the same failure mode that `attemptGuard` prevents
 * on the attempt flow.
 *
 * A job is signable when it matches either the **Ready to Sign** or **Review Signed**
 * state:
 * - `jobStatus === 1` — job is served/closed in the system
 * - `manner > 0` — has been served via some manner
 * - `approvedStatus === 0` — not yet approved by the back-office
 * - `esignStatus === 'N' | 'R'` — awaiting first signature or under TL review
 *
 * @example
 * ```ts
 * // In app.routes.ts
 * { path: 'affidavit/:id', canActivate: [authGuard, signGuard] }
 * ```
 */
export const signGuard: CanActivateFn = (route) => {
  const jobsService = inject(JobsService);
  const router      = inject(Router);

  const jobId = route.paramMap.get('id') ?? '';
  const job   = jobsService.getJob(jobId);

  // Job not found (e.g. direct URL access before sync completes) — send to sign queue.
  if (!job) return router.createUrlTree(['/sign']);

  const signable =
    job.jobStatus === 1 &&
    job.manner > 0 &&
    job.approvedStatus === 0 &&
    (job.esignStatus === 'N' || job.esignStatus === 'R');

  return signable ? true : router.createUrlTree(['/sign']);
};
